<template>
  <w-sliding-popup ref="slidingPopup" title="필터 추가" width="600px" @close="close">
    <div slot="content">
      <div class="title-h4">필터 기본 정보</div>
      <el-row>
        <div class="mr-b25">
          <el-form ref="form" :model="{ filterName, filterDesc, filterGroupId }" label-position="top" :rules="formRules">
            <el-row>
              <el-form-item label="필터 이름" prop="filterName">
                <el-input v-model="filterName"></el-input>
              </el-form-item>
            </el-row>
            <el-row>
              <el-form-item label="필터 그룹" prop="filterGroupId">
                <el-select v-model="filterGroupId">
                  <el-option v-for="group in filterGroups" :key="group.filterGroupId" :label="group.filterGroupName" :value="group.filterGroupId"></el-option>
                </el-select>
              </el-form-item>
            </el-row>
            <el-row>
              <el-form-item label="필터 설명" prop="filterDesc">
                <el-input type="textarea" v-model="filterDesc" resize="none"></el-input>
              </el-form-item>
            </el-row>
          </el-form>
        </div>
      </el-row>
      <el-row type="flex" justify="space-between">
        <div class="vertical-center-box">
          <div class="title-bar title-h4">필터 검색 조건</div>
        </div>
      </el-row>
      <el-row>
        <w-filter-condition-group-table ref="conditionTable" v-for="group in filterTableDatas"
        :key="group.groupId"
        :group-id="group.groupId"
        :group-number="group.groupNumber"
        :dataindex-id="group.dataindexId"
        :conditions="group.conditions"
        :search-tags="searchTags"
        popup-type="ADD"
        @delete-group="deleteConditionGroup"
        @add-group="addFilterGroup"></w-filter-condition-group-table>
      </el-row>
    </div>
    <div slot="footer">
      <el-button type="solid" size="large" @click="onSaveButton">저장</el-button>
      <el-button type="content" size="large" @click="clickClose">취소</el-button>
    </div>
  </w-sliding-popup>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import remove from 'lodash/remove';
import findIndex from 'lodash/findIndex';
import every from 'lodash/every';
import forEach from 'lodash/forEach';
import querystring from 'querystring';
import { mapGetters } from 'vuex';

import msgUtils from '@/utils/message';
import LogAPI from '@/api/log/integrated-log';
// import LogAPI from '@/api/log/common';
import WFilterConditionGroupTable from '@/views/log-analysis/components/FilterConditionGroupTable';

export default {
  name: 'WFilterAddPopup',
  components: {
    WFilterConditionGroupTable,
  },
  props: {
    /**
     * 로그 조회 테이블에서 태그로 띄운 검색 조건 배열 데이터
     * @type {Object}
     */
    searchTags: {
      type: Array,
    },
    filterGroup: {
      type: Object,
      default: null,
    },
  },
  data() {
    const filterNameRule = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('필수 정보입니다.'));
      }
      if (value.length > 32) {
        return callback(new Error('32자 이내로 입력해주세요.'));
      }
      return callback();
    };
    const filterDescRule = (rule, value, callback) => {
      if (value.length > 255) {
        return callback(new Error('255자 이내로 입력해주세요.'));
      }
      return callback();
    };
    const formRules = {
      filterName: [
        { required: true, validator: filterNameRule, trigger: 'blur' },
      ],
      filterGroupId: [
        { required: true, trigger: 'change', message: '필터 그룹을 선택해주세요.' },
      ],
      filterDesc: [
        { validator: filterDescRule, trigger: 'blur' },
      ],
    };
    return {
      filterName: '', // 필터 이름
      filterDesc: '',
      hasFilterGroup: true,
      filterGroupId: null,
      filterGroups: [],
      orderbyIndex: 0,
      formRules,
      groupNumber: 1,
      groupId: 1,
      filterTableDatas: [],
      filterTableDatasOrigin: [],
    };
  },
  computed: mapGetters(['selectedNode']),
  watch: {
    selectedNode: {
      immediate: true,
      handler() {
        this.filterInformation = {};
        this.filterName = '';
        this.filterDesc = '';
        this.groupNumber = 1;
        this.initData();
      },
    },
    filterGroupId(value) {
      // max orderbyIndex 조회
      if (!isNil(value)) {
        this.getMaxOrderbyIndex(value);
      }
    },
  },
  methods: {
    /**
     * 필터 그룹 선택 콤보박스 옵션 데이터 조회
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadFilterGroup() {
      LogAPI.getLogFilterGroups().then((response) => {
        if (response.data.success) {
          this.filterGroups = response.data.data;
          if (isEmpty(this.filterGroups)) {
            this.$message(msgUtils.getCustomMsgData('먼저 필터 그룹을 생성해주세요.'));
          } else if (isEmpty(this.filterGroup)) {
            this.filterGroupId = this.filterGroups[0].filterGroupId;
          } else {
            this.filterGroupId = this.filterGroup.filterGroupId;
          }
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필터 그룹', false));
        throw new Error(e);
      });
    },
    getMaxOrderbyIndex(filterGroupId) {
      const params = {
        params: {
          filterGroupId,
        },
      };
      LogAPI.getMaxOrderbyIndex(params).then((response) => {
        if (response.data.success) {
          if (!isEmpty(response.data.data)) {
            this.orderbyIndex = response.data.data.orderbyIndex + 1;
          } else {
            this.orderbyIndex = 1;
          }
        }
      }).catch((e) => {
        throw new Error(e);
      });
    },
    /**
     * 검색조건 테이블 데이터를 초기화한다. (filterInformation이 비어있을 경우)
     * @author Minyeong
     * @return {[type]} [description]
     */
    initData() {
      if (!isEmpty(this.searchTags)) {
        this.filterTableDatas = [
          {
            groupNumber: this.groupNumber,
            groupId: this.groupId,
            dataindexId: 'log-*',
            conditions: [],
          },
        ];
        const conditions = [
          { fieldId: 'originMessage', fieldValue: '', negationOperatorFlag: 'N', orderbyIndex: 1, regexFlag: 'N' },
        ];
        for (let i = 0; i < this.filterTableDatas.length; i += 1) {
          this.filterTableDatas[i].conditions = conditions;
        }
      } else {
        const initCond = { fieldId: 'originMessage', fieldValue: '', negationOperatorFlag: 'N', orderbyIndex: 1, regexFlag: 'N' };
        this.filterTableDatas = [];
        this.filterTableDatas.push(
          {
            groupNumber: this.groupNumber,
            groupId: this.groupId,
            dataindexId: 'log-*', // TODO
            conditions: [initCond],
          },
        );
      }
    },
    /**
     * 검색조건 그룹 테이블을 추가한다.
     * @author Minyeong
     */
    addFilterGroup(fieldOptions) {
      this.groupId += 1;
      this.groupNumber += 1;
      this.filterTableDatas.push(
        {
          groupNumber: this.groupNumber,
          groupId: this.groupId,
          dataindexId: 'log-*', // TODO
          conditions: [
            { fieldId: fieldOptions[0].fieldId, fieldValue: '', negationOperatorFlag: 'N', orderbyIndex: 1, regexFlag: 'N' },
          ],
        },
      );
    },
    /**
     * 저장 버튼 클릭 시 > 필터 추가 / 필터 수정
     * @author Minyeong
     * @return {[type]} [description]
     */
    onSaveButton() {
      this.$refs.form.validate().then((success) => {
        if (success) {
          const filterParams = {
            filterId: this.selectedNode.objId,
            filterName: this.filterName,
            filterDesc: this.filterDesc,
            filterGroupId: this.filterGroupId,
            orderbyIndex: this.orderbyIndex,
          };
          const conditionTables = this.$refs.conditionTable;
          // 필터 추가
          const payload = [];
          for (let i = 0; i < conditionTables.length; i += 1) {
            // 그룹별로 groupId와 conditions를 가져온다.
            const params = conditionTables[i].getGroupConditionParams();
            if (!every(params.condition, (item) => !isEmpty(item.fieldValue) && !isEmpty(item.fieldId))) {
              this.$message(msgUtils.getCustomMsgData('검색 조건을 입력해주세요.'));
              return;
            }
            forEach(params.condition, (item) => {
              if (item.fieldValue.length > 255) {
                this.$message(msgUtils.getCustomMsgData('검색값은 255자 이내로 입력해주세요.'));
                return;
              }
            });
            params.condition.forEach((value) => {
              const cond = {
                groupId: params.groupId,
                dataindexId: params.dataindexId,
                fieldId: value.fieldId,
                fieldValue: value.fieldValue,
                negationOperatorFlag: value.negationOperatorFlag,
                orderbyIndex: value.orderbyIndex,
                regexFlag: value.regexFlag,
              };
              payload.push(cond);
            });
          }
          LogAPI.insertFilter(querystring.stringify(filterParams), payload).then((response) => {
            if (response.data.success) {
              this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, '필터', response.data.success));
              this.$refs.slidingPopup.close();
              this.$emit('reload-tree-data');
            } else {
              this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, '필터', response.data.success));
            }
          }).catch((e) => {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, '필터', false));
            throw new Error(e);
          });
        }
      }).catch(() => {});
    },
    /**
     * 검색조건 그룹을 삭제한다.
     * @author Minyeong
     * @param  {[type]} groupId [description]
     * @return {[type]}         [description]
     */
    deleteConditionGroup(groupId) {
      const groupIndex = findIndex(this.filterTableDatasOrigin, { groupId });
      if (groupIndex !== -1) {
        // 삭제된 그룹
        this.deleteGroup.push({ groupId, filterId: this.selectedNode.objId });
        this.addCondition = remove(this.addCondition, (n) => n.groupId !== groupId);
        this.updateCondition = remove(this.updateCondition, (n) => n.groupId !== groupId);
        this.deleteCondition = remove(this.deleteCondition, (n) => n.groupId !== groupId);
      }
      const groupNumber = this.filterTableDatas.filter((n) => n.groupId === groupId)[0].groupNumber;
      this.filterTableDatas = remove(this.filterTableDatas, (n) => n.groupId !== groupId);
      this.filterTableDatas.forEach((value) => {
        if (value.groupNumber > groupNumber) {
          value.groupNumber -= 1;
        }
      });
      this.groupNumber -= 1;
    },
    /**
     * 팝업 오픈 함수
     * @author Minyeong
     * @return {[type]} [description]
     */
    open(orderbyIndex) {
      if (!isNil(orderbyIndex)) {
        this.orderbyIndex = orderbyIndex + 1;
      } else {
        this.orderbyIndex = 1;
      }
      this.loadFilterGroup();
      this.groupNumber = 1;
      this.initData();
      this.$refs.slidingPopup.open();
    },
    /**
     * 팝업 닫기
     * @author Minyeong
     * @return {[type]} [description]
     */
    close() {
      const clear = ['filterName', 'filterGroupId', 'filterDesc'];
      this.$refs.form.clearValidate(clear);
      const conditionTables = this.$refs.conditionTable;
      conditionTables.forEach((table) => {
        table.initUpdateCondition();
      });
      this.addCondition = [];
      this.deleteCondition = [];
      this.updateCondition = [];
      this.deleteGroup = [];
      this.groupNumber = 1;
      this.groupId = 1;
      this.filterName = '';
      this.filterDesc = '';
    },
    clickClose() {
      this.$refs.slidingPopup.close();
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log-analysis/popup/LogFilterAddPopup.vue